import React from 'react';
import PropTypes from 'prop-types';
import { Controller } from 'react-hook-form';
import Fields from '../editors';
import { buildFieldRules } from '../../utils/fieldValidation';

// Bridge between react-hook-form and the editor field components.
//
// The 27 components in ../editors share a contract that predates RHF:
//
//     { field, value, data, mode, context, disabled, onChange(id, value) }
//
// Rather than rewrite all of them to register with RHF — and break the two other
// dialogs that render the same registry with their own state (ProfileDialog,
// CreateTransactionDialog) — this maps RHF's controlled value onto that contract.
// A field component needs no knowledge of RHF at all.
//
// Components that want to participate in validation can additionally read the
// `error` and `onBlur` props passed here; the rest ignore them harmlessly.

const FormField = ({
    field,
    control,
    values,
    mode,
    context,
    locked,
    autoFocus,
    onAfterChange
}) => {
    const Component = Fields[field.cell] || Fields.Text;
    // Derived here rather than plumbed in, so there is one place a field's rules
    // come from.
    const rules = buildFieldRules(field);

    return (
        <Controller
            name={field.id}
            control={control}
            rules={rules}
            render={({ field: rhf, fieldState }) => (
                <Component
                    field={{
                        ...field,
                        disabledByTemplate: locked,
                        required: Boolean(rules?.required)
                    }}
                    disabled={Boolean(locked) || field.disabled === true}
                    // Whole-form values: DialogAmountField reads `data.currency`
                    // for its symbol, and read-only renderers use it too.
                    data={values}
                    value={rhf.value}
                    mode={mode}
                    context={context}
                    autoFocus={autoFocus}
                    error={fieldState.error?.message}
                    onBlur={rhf.onBlur}
                    onChange={(id, value) => {
                        if (locked) return;
                        rhf.onChange(value);
                        onAfterChange?.(field.id, value);
                    }}
                />
            )}
        />
    );
};

FormField.propTypes = {
    field: PropTypes.object.isRequired,
    control: PropTypes.object.isRequired,
    values: PropTypes.object,
    mode: PropTypes.oneOf(['add', 'edit']),
    context: PropTypes.object,
    /** Field is filled from a template and must not be edited. */
    locked: PropTypes.bool,
    autoFocus: PropTypes.bool,
    /** Called after a value changes, for cross-field effects. */
    onAfterChange: PropTypes.func
};

export default FormField;
